// src/firebase.ts
import { initializeApp } from "firebase/app";
import { getAuth, setPersistence, browserLocalPersistence, browserSessionPersistence } from "firebase/auth";
import { initializeFirestore, memoryLocalCache } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { captureMessage } from "./lib/sentry";

const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID as string;
const envAuthDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string;

// La app se sirve desde varias direcciones (dominio propio, .web.app y
// .firebaseapp.com) y todas tienen las páginas de login de Firebase (/__/auth/*).
// Usamos como authDomain la MISMA dirección desde la que se abrió la app: así el
// redirect de Google vuelve al mismo origen y Safari/Chrome no pierden el
// resultado por aislar el almacenamiento entre dominios (ITP / storage partitioning).
const SAME_ORIGIN_AUTH_HOSTS = [envAuthDomain, `${projectId}.web.app`, `${projectId}.firebaseapp.com`];
const currentHost = typeof window !== "undefined" ? window.location.hostname : "";
const authDomain = SAME_ORIGIN_AUTH_HOSTS.includes(currentHost) ? currentHost : envAuthDomain;

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain,
  projectId,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
};

export const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);

// Persistencia durable (fallback a session si el storage está restringido, ej. iOS PWA).
// OJO: en Safari, si IndexedDB queda en mal estado (bug conocido de WebKit), esta llamada
// puede quedar colgada para siempre — nunca resuelve NI rechaza, así que el .catch() de abajo
// nunca se dispara y Auth queda "a medio configurar" para toda la sesión, afectando cualquier
// método de login posterior. Por eso corremos ambos intentos en paralelo con un límite de
// tiempo, en vez de encadenarlos: si el primero no resuelve rápido, no bloqueamos nada.
function setupAuthPersistence() {
  const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error("timeout")), 5000));
  Promise.race([setPersistence(auth, browserLocalPersistence), timeout])
    .then(() => captureMessage("Auth persistence: local (durable)", "info"))
    .catch(() => {
      captureMessage("Auth persistence: local FALLÓ o colgó, cae a session-only", "warning");
      setPersistence(auth, browserSessionPersistence)
        .then(() =>
          captureMessage("Auth persistence: session-only activa (no sobrevive a cerrar el navegador)", "warning")
        )
        .catch(() => {
          // Si ni siquiera esto resuelve, seguimos igual: Firebase Auth usa memoria en
          // el peor caso y el login puede funcionar (solo no sobrevive a un refresh).
          captureMessage("Auth persistence: ninguna pudo configurarse (memoria)", "error");
        });
    });
}
setupAuthPersistence();
auth.useDeviceLanguage?.();

// Pide al navegador que trate el almacenamiento (IndexedDB, donde vive la sesión)
// como persistente, para que no lo borre por falta de espacio. No evita el borrado
// de Safari a los 7 días sin uso en una pestaña (ITP), pero en la app instalada en
// pantalla de inicio sí ayuda a que la sesión sobreviva.
navigator.storage?.persist?.().catch(() => {});

// Caché de Firestore EN MEMORIA (la por defecto), a propósito NO persistente:
// la caché en IndexedDB (persistentLocalCache) se probó y rompía el login en
// celulares — si el navegador cierra/vacía esa base con la app abierta (falta de
// espacio, Safari, "borrar datos del sitio"), Firestore se niega a seguir
// ("refusing to open IndexedDB database due to potential corruption") hasta
// recargar, y todas las lecturas/escrituras quedan colgadas.
// (Debe ser la PRIMERA llamada que toca Firestore en toda la app — otros
// archivos hacen getFirestore(app) de nuevo y reciben esta misma instancia.)
export const db = initializeFirestore(app, {
  localCache: memoryLocalCache(),
});
export const storage = getStorage(app);
